import { isDouble, tileEquals, type Arm, type Chain, type Tile } from './types.js';

/**
 * Pure chain operations. No knowledge of hands, scoring, or turn rotation —
 * just the tile-placement state machine. `Chain` is treated as immutable;
 * every operation returns a fresh value.
 *
 * Arm-index conventions:
 * - Before a spinner exists: arms 0 and 1 only, extending in opposite
 *   directions from the lead tile.
 * - Once a spinner exists: arms 0/1 stay, and arms 2 and 3 are appended as
 *   the perpendicular arms — legal play targets, but latent (not counted)
 *   until a tile is played on them.
 *
 * **The spinner is the first double to be COVERED, not the first played.**
 * A double only opens its long sides once the line has grown past it, so
 * that is the moment the branch is decided — playing a double early no
 * longer reserves the branch for the rest of the hand while the layout
 * grows past some other double. See `findCoveredDouble`.
 *
 * Latent vs active: `activeArms` lists arm indices whose open value
 * contributes to the running sum used for scoring. A latent arm is
 * playable; the moment a tile is played on it, it becomes active.
 */

export const EMPTY_CHAIN: Chain = {
  lead: null,
  spinner: null,
  arms: [],
  activeArms: [],
};

export interface ApplyResult {
  readonly chain: Chain;
  /** The new sum of open-end values across all active arms (for scoring). */
  readonly openEndsSum: number;
  /**
   * True if this play formed the spinner — i.e. it covered the first double
   * to be covered. A play that merely *lays* a double no longer forms one.
   */
  readonly spinnerFormed: boolean;
}

/**
 * Apply the very first tile in a hand: sets `lead`. Never sets `spinner` —
 * even a double lead has both faces open until each side is built on, and
 * an uncovered double is not a branch point.
 */
export function applyLead(chain: Chain, tile: Tile): ApplyResult {
  if (chain.lead !== null) {
    throw new Error('applyLead: chain already has a lead');
  }
  if (isDouble(tile)) {
    // mainAxis carries the double's full pip-sum (= 2×face) for scoring.
    // arms[0] and arms[1] share the same object so sumActive can dedup the
    // value by reference identity — counted once while still uncovered, and
    // automatically split off (replaced) when a tile is played on one side.
    const mainAxis: Arm = { tiles: [tile], openValue: tile[0] + tile[1] };
    const next: Chain = {
      lead: tile,
      spinner: null,
      arms: [mainAxis, mainAxis],
      activeArms: [0, 1],
    };
    return { chain: next, openEndsSum: sumActive(next), spinnerFormed: false };
  }
  const arm0: Arm = { tiles: [tile], openValue: tile[0] };
  const arm1: Arm = { tiles: [tile], openValue: tile[1] };
  const next: Chain = {
    lead: tile,
    spinner: null,
    arms: [arm0, arm1],
    activeArms: [0, 1],
  };
  return { chain: next, openEndsSum: sumActive(next), spinnerFormed: false };
}

/**
 * Play a tile on arm `armIndex`. The tile must connect — one of its pips
 * must equal the arm's current match value (the face of the outer tile, or
 * the arm's openValue when the outer tile is a non-double). Throws on
 * illegal play; callers should validate via `canPlayOnArm` first.
 * `spinnerEnabled` is the rule set's `spinner` flag — when false no double
 * ever branches and the chain stays a line on arms 0/1.
 */
export function applyPlay(
  chain: Chain,
  armIndex: number,
  tile: Tile,
  spinnerEnabled: boolean,
): ApplyResult {
  const arm = chain.arms[armIndex];
  if (arm === undefined) {
    throw new Error(`applyPlay: arm ${armIndex} does not exist`);
  }
  if (!canPlayOnArm(chain, armIndex, tile)) {
    throw new Error(
      `applyPlay: tile [${tile[0]},${tile[1]}] cannot match arm value ${matchValueFor(arm)}`,
    );
  }

  const matchValue = matchValueFor(arm);
  const tileIsDouble = isDouble(tile);
  let newOpenValue: number;
  if (tileIsDouble) {
    newOpenValue = tile[0] + tile[1];
  } else if (tile[0] === matchValue) {
    newOpenValue = tile[1];
  } else {
    newOpenValue = tile[0];
  }

  const updatedArm: Arm = {
    tiles: [...arm.tiles, tile],
    openValue: newOpenValue,
  };

  const arms = chain.arms.slice();
  arms[armIndex] = updatedArm;
  const activeArms = chain.activeArms.slice();
  let spinner = chain.spinner;

  // The branch is decided the moment a double is covered — this play may be
  // the one that covered it. Once a spinner exists there is nothing to
  // decide: a hand has exactly one branch point.
  const newSpinner =
    spinnerEnabled && spinner === null ? findCoveredDouble(chain.lead, arms) : null;
  const spinnerFormingHere = newSpinner !== null;
  if (newSpinner !== null) {
    spinner = newSpinner;
    // Separate objects so identity dedup in sumActive doesn't collapse them.
    // Perp arms stay latent (not in activeArms) until a tile is actually
    // played on them — open-end scoring counts only ends that exist
    // physically on the layout. They ARE legal targets immediately, since a
    // spinner is covered by construction.
    const perpA: Arm = { tiles: [], openValue: newSpinner[0] };
    const perpB: Arm = { tiles: [], openValue: newSpinner[0] };
    arms.push(perpA, perpB);
  }
  if (!activeArms.includes(armIndex)) {
    // Play on a previously-latent arm activates it.
    activeArms.push(armIndex);
  }

  const next: Chain = {
    lead: chain.lead,
    spinner,
    arms,
    activeArms,
  };
  return { chain: next, openEndsSum: sumActive(next), spinnerFormed: spinnerFormingHere };
}

/** Whether a tile can legally be placed on the given arm. */
export function canPlayOnArm(chain: Chain, armIndex: number, tile: Tile): boolean {
  const arm = chain.arms[armIndex];
  if (arm === undefined) return false;
  if (armIndex >= 2 && !isSpinnerCovered(chain)) return false;
  const match = matchValueFor(arm);
  return tile[0] === match || tile[1] === match;
}

/**
 * The double the layout has grown past, and therefore the hand's branch
 * point. Called on every play until one is found; the first hit wins and
 * `applyPlay` freezes it as the spinner.
 *
 * "Covered" differs by position, because it means "no longer an open end":
 * - **Lead double** — both faces are open ends until BOTH main arms carry a
 *   play beyond it (`tiles.length >= 2`, where `tiles[0]` is the shared lead).
 * - **Mid-chain double** — its incoming side is the tile it connected to, so
 *   it is covered as soon as one more tile follows it on the same arm.
 *
 * Only arms 0/1 are scanned: the perpendicular arms don't exist until a
 * spinner has been chosen, and by then this function is no longer called.
 */
export function findCoveredDouble(lead: Tile | null, arms: readonly Arm[]): Tile | null {
  const arm0 = arms[0];
  const arm1 = arms[1];
  if (!arm0 || !arm1) return null;
  if (
    lead !== null &&
    isDouble(lead) &&
    arm0.tiles.length >= 2 &&
    arm1.tiles.length >= 2
  ) {
    return lead;
  }
  for (const arm of [arm0, arm1]) {
    // Stop one short of the end — the outermost tile is by definition still
    // an open end. `tiles[0]` is the lead, which the branch above owns.
    for (let i = 0; i < arm.tiles.length - 1; i += 1) {
      const tile = arm.tiles[i];
      if (tile === undefined || !isDouble(tile)) continue;
      if (lead !== null && tileEquals(tile, lead)) continue;
      return tile;
    }
  }
  return null;
}

/**
 * Whether the perpendicular arms may be played on. Equivalent to "a spinner
 * exists", since the spinner is only chosen at the moment it becomes
 * covered — kept as a named predicate because the layout and the legality
 * check both read as "are the side arms live yet".
 */
export function isSpinnerCovered(chain: Chain): boolean {
  return chain.spinner !== null;
}

/**
 * The pip value the next tile must match. For a non-double outer tile this
 * is just `arm.openValue` (= the non-matching half). For a double outer
 * tile this is the *face* (e.g. 6 for [6|6]) — not `2×face`, which is what
 * `arm.openValue` carries for scoring purposes. For latent perpendicular
 * arms (no outer tile yet) we fall back to `arm.openValue`, which the
 * spinner-formation code already populates with the spinner face.
 */
export function matchValueFor(arm: Arm): number {
  const outer = arm.tiles[arm.tiles.length - 1];
  if (outer && isDouble(outer)) return outer[0];
  return arm.openValue;
}

/** Sum the open-end values of all active arms (scoring-relevant). */
export function sumActive(chain: Chain): number {
  let total = 0;
  // Dedup by reference identity: a spinner-lead's two main-axis arms share
  // the same Arm object until one side is played, so the double's pip-sum
  // contributes once. Once applyPlay replaces an arm with a fresh object,
  // the dedup naturally stops applying.
  const seen = new Set<Arm>();
  for (const idx of chain.activeArms) {
    const arm = chain.arms[idx];
    if (!arm || seen.has(arm)) continue;
    seen.add(arm);
    total += arm.openValue;
  }
  return total;
}

/** All arm indices currently legal as play targets (active + latent). */
export function legalArmIndices(chain: Chain): readonly number[] {
  const covered = isSpinnerCovered(chain);
  return chain.arms
    .map((_, i) => i)
    .filter((i) => i < 2 || covered);
}
